import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Linkedin, Twitter, Youtube } from "lucide-react";

import { Media } from "./Media";
import { Button } from "./ui/button";
import { NewsletterSignup } from "./NewsletterSignup";
import { CookiePreferences } from "./CookiePreferences";
import { trackDemoClick } from "@/lib/telemetry";
import {
  COMPANY_STATEMENT,
  DEMO_URL,
  LEGAL_LINKS,
  NAV_GROUPS,
  SOCIAL_LINKS,
  isExternalHref,
} from "@/lib/navigation";
import logo from "@/assets/datasyte-logo-white.webp";

const socialIcon = (icon: (typeof SOCIAL_LINKS)[number]["icon"]) => {
  if (icon === "linkedin") return <Linkedin size={20} aria-hidden="true" />;
  if (icon === "youtube") return <Youtube size={20} aria-hidden="true" />;
  return <Twitter size={20} aria-hidden="true" />;
};

const legalLinkClass =
  "text-xs text-accent hover:underline inline-flex items-center min-h-11 py-2";

export const Footer = () => (
  <footer className="band-dark border-t border-border pt-14 pb-10 mt-10">
    <div className="container mx-auto px-6">
      <div className="grid gap-10 lg:grid-cols-[1.4fr_2.6fr]">
        <div>
          <Media
            src={logo}
            alt="datasyteAI™ by PathFree Technologies logo"
            className="mb-4 h-12 w-auto object-contain"
            sizes="230px"
          />
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">PathFree Technologies Corporation</strong> develops{" "}
            <strong className="text-foreground">datasyteAI™</strong>, the clinical intelligence layer,
            and <strong className="text-foreground">AiMediQ™</strong>, the intelligent Code Cart it
            powers.
          </p>
          <Button asChild size="lg" className="mt-6 w-full sm:w-auto">
            <a href={DEMO_URL} onClick={() => trackDemoClick("footer")}>
              Request a Demo
              <ArrowRight className="ml-2" size={18} aria-hidden="true" />
            </a>
          </Button>

          <div className="mt-6 text-sm">
            <h2 className="mb-2 font-bold text-foreground">Contact</h2>
            <p>
              <a href="mailto:info@pathfree.com" className="text-accent underline underline-offset-2">
                info@pathfree.com
              </a>
            </p>
            <p className="mt-1">
              <a href="tel:+19499033788" className="text-accent underline underline-offset-2">
                +1 (949) 903-3788
              </a>
            </p>
            <p className="mt-1 text-muted-foreground">
              PathFree Technologies Corporation
              <br />
              19800 MacArthur Blvd, Unit 300, Irvine, CA 92612, USA
            </p>
          </div>

          {/* Evaluation, security, and procurement routing — what review teams look for first. */}
          <div className="mt-6 text-sm">
            <h2 className="mb-2 font-bold text-foreground">Evaluation and procurement</h2>
            <p className="text-muted-foreground">
              Security reviews, documentation requests, and diligence questions are handled through
              the{" "}
              <Link to="/trust-center" className="text-accent underline underline-offset-2">
                Trust Center
              </Link>
              . Deployment planning is outlined in{" "}
              <Link to="/enterprise-deployment" className="text-accent underline underline-offset-2">
                Enterprise Deployment
              </Link>
              .
            </p>
            <p className="mt-2 text-muted-foreground">
              Report a suspected security vulnerability to{" "}
              <a href="mailto:info@pathfree.com" className="text-accent underline underline-offset-2">
                info@pathfree.com
              </a>{" "}
              with the subject line “Security.” Please do not include patient information.
            </p>
          </div>

          <div className="mt-6">
            <h2 className="mb-2 font-bold text-foreground">Follow PathFree</h2>
            <div className="flex flex-wrap gap-3">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border text-accent transition-colors hover:bg-accent/10"
                >
                  {socialIcon(social.icon)}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div>
          <nav aria-label="Footer" className="grid gap-x-8 gap-y-2 sm:grid-cols-2 xl:grid-cols-3">
            {[
              ...NAV_GROUPS.map((group) => ({
                id: group.id,
                label: group.label,
                items: group.items,
              })),
              {
                id: "corporate",
                label: "Corporate",
                items: [
                  { href: "/corporate/investor-information", label: "Investor Information" },
                  { href: "https://pathfree.com", label: "PathFree Technologies" },
                ],
              },
            ].map((group) => (
              <details key={group.id} className="group self-start border-b border-border/60">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-2 py-3 text-sm font-bold uppercase tracking-wide text-foreground">
                  {group.label}
                  <ChevronDown
                    size={16}
                    aria-hidden="true"
                    className="shrink-0 text-accent transition-transform duration-200 group-open:rotate-180"
                  />
                </summary>
                <ul className="flex flex-col pb-3">
                  {group.items.map((item) => (
                    <li key={item.href}>
                      {isExternalHref(item.href) ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex min-h-9 items-center py-1 text-sm text-accent hover:underline"
                        >
                          {item.label}
                        </a>
                      ) : (
                        <Link
                          to={item.href}
                          className="inline-flex min-h-9 items-center py-1 text-sm text-accent hover:underline"
                        >
                          {item.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </nav>


          <div className="mt-8 max-w-xl">
            <h2 className="mb-2 font-bold text-foreground">Stay in the loop</h2>
            <NewsletterSignup />
          </div>
        </div>
      </div>

      <div className="mt-12 border-t border-border pt-6 space-y-3">
        <p className="text-xs text-muted-foreground">
          <strong className="text-foreground">Regulatory status:</strong> AiMediQ™ powered by
          datasyteAI™ is currently in the pre-market development and validation phase. It is not
          cleared or approved by the FDA or any other regulatory authority, is not available for
          sale, and is not for clinical use. Use is limited to investigational, research,
          simulation, and training purposes.
        </p>
        <p className="text-xs text-muted-foreground">
          <strong className="text-foreground">Not medical advice:</strong> Content on this site is
          informational only and is not a substitute for the judgment of a licensed healthcare
          professional or your institution's protocols. In an emergency, contact local emergency
          services.
        </p>
        <p className="text-xs text-muted-foreground">
          <strong className="text-foreground">Forward-looking statements:</strong> Product, market,
          regulatory, and financial statements are forward-looking and subject to change. This site
          is not an offer to sell or a solicitation to buy any security. See the{" "}
          <Link
            to="/disclaimers"
            className="text-accent underline decoration-accent/50 underline-offset-2 hover:decoration-accent"
          >
            full disclaimers
          </Link>
          .
        </p>
        <p className="text-xs text-muted-foreground">
          <strong className="text-foreground">Trademarks:</strong> {COMPANY_STATEMENT} datasyteAI™
          and AiMediQ™ are trademarks of PathFree Technologies Corporation. All other marks are the
          property of their respective owners.
        </p>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-border pt-4">
        <p className="text-xs text-muted-foreground">
          &copy; 2026 PathFree Technologies Corporation | Irvine, California
        </p>
        {LEGAL_LINKS.map((link) => (
          <Link key={link.href} to={link.href} className={legalLinkClass}>
            {link.label}
          </Link>
        ))}
        <CookiePreferences className={legalLinkClass} />
      </div>
    </div>
  </footer>
);
